import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { useLazyQuery, useQuery, useMutation } from '@apollo/client';
import { GET_MEMBERS, GET_ALL_MEMBERS, GET_MEMBER, UPDATE_MEMBER, CREATE_MEMBER, CREATE_INVITATION, DELETE_MEMBER } from '../services/members';
import AppIcon from '../components/AppIcon';
import LocationPicker from '../components/LocationPicker';
import { Search, User, ChevronLeft, ChevronRight, FileText, UserCircle, Save, X, Plus, Mail, Send } from 'lucide-react';
import authStorage from '../store/authStorage';

const PAGE_SIZE = 20;

export default function MembersPage() {
  const [selectedId, setSelectedId] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [offset, setOffset] = useState(0);
  const [activeTab, setActiveTab] = useState('profile');
  const [showDetail, setShowDetail] = useState(false);
  const [showAddMember, setShowAddMember] = useState(false);
  const [showInvite, setShowInvite] = useState(false);

  // Debounce search
  useEffect(() => {
    const timer = setTimeout(() => setDebouncedSearch(searchQuery), 300);
    return () => clearTimeout(timer);
  }, [searchQuery]);

  // Reset offset when search changes
  useEffect(() => {
    setOffset(0);
  }, [debouncedSearch]);

  // Parse search into firstName/lastName
  const searchParts = useMemo(() => {
    const trimmed = debouncedSearch.trim();
    if (!trimmed) return {};
    const parts = trimmed.split(/\s+/);
    if (parts.length === 1) return { firstName: parts[0] };
    return { firstName: parts[0], lastName: parts.slice(1).join(' ') };
  }, [debouncedSearch]);

  // Get current user's personId for identification
  const myId = (() => {
    try {
      const token = authStorage.getToken();
      if (token) {
        const payload = JSON.parse(atob(token.split('.')[1]));
        const pid = payload.personId || localStorage.getItem('mien_person_id');
        return pid ? String(pid) : null;
      }
    } catch {}
    const stored = localStorage.getItem('mien_person_id');
    return stored ? String(stored) : null;
  })();

  // Get current user's personId for invitation
  const getPersonId = () => myId;

  // Fetch members list
  const [fetchMembers, { data: membersData, loading: membersLoading }] = useLazyQuery(GET_MEMBERS, {
    fetchPolicy: 'network-only',
  });

  useEffect(() => {
    fetchMembers({
      variables: {
        offset,
        limit: PAGE_SIZE,
        ...searchParts,
      },
    });
  }, [fetchMembers, offset, searchParts]);

  const rawMembers = membersData?.members?.members || [];
  const total = membersData?.members?.total || 0;
  const totalPages = Math.ceil(total / PAGE_SIZE);
  const currentPage = Math.floor(offset / PAGE_SIZE) + 1;

  // Sort: current user first, then alphabetical
  const members = useMemo(() => {
    if (!myId) return rawMembers;
    return [...rawMembers].sort((a, b) => {
      if (a.id === myId) return -1;
      if (b.id === myId) return 1;
      return (a.firstName + ' ' + a.lastName).localeCompare(b.firstName + ' ' + b.lastName);
    });
  }, [rawMembers, myId]);

  // Build a map for relationship lookup
  const memberMap = useMemo(() => {
    const map = {};
    rawMembers.forEach(m => { map[m.id] = m; });
    return map;
  }, [rawMembers]);

  // Compute relationship label of a member relative to me
  const getRelationToMe = useCallback((member) => {
    if (!myId || member.id === myId) return null;
    const me = memberMap[myId];
    if (!me) return null;

    // Direct parent
    if (member.id === me.motherId) return member.gender === 'male' ? 'Father' : 'Mother';
    if (member.id === me.fatherId) return member.gender === 'male' ? 'Father' : 'Mother';

    // Direct child (I am their parent)
    if (me.id === member.motherId) return member.gender === 'male' ? 'Son' : 'Daughter';
    if (me.id === member.fatherId) return member.gender === 'male' ? 'Son' : 'Daughter';

    // Spouse
    if (me.spouseId === member.id || member.spouseId === me.id) return member.gender === 'male' ? 'Husband' : 'Wife';

    // Grandparent (my parent's parent)
    const myMother = memberMap[me.motherId];
    const myFather = memberMap[me.fatherId];

    // Step-parent check must come BEFORE grandparent check
    // because myMother.spouseId could match either a step-father or grandfather
    if (myMother && myMother.spouseId === member.id && member.id !== me.fatherId) return member.gender === 'male' ? 'Step-Father' : 'Step-Mother';
    if (myFather && myFather.spouseId === member.id && member.id !== me.motherId) return member.gender === 'male' ? 'Step-Father' : 'Step-Mother';

    if (myMother) {
      if (member.id === myMother.motherId) return member.gender === 'male' ? 'Grandfather' : 'Grandmother';
      if (member.id === myMother.fatherId) return member.gender === 'male' ? 'Grandfather' : 'Grandmother';
      if (member.id === myMother.spouseId) return member.gender === 'male' ? 'Grandfather' : 'Grandmother';
    }
    if (myFather) {
      if (member.id === myFather.motherId) return member.gender === 'male' ? 'Grandfather' : 'Grandmother';
      if (member.id === myFather.fatherId) return member.gender === 'male' ? 'Grandfather' : 'Grandmother';
      if (member.id === myFather.spouseId) return member.gender === 'male' ? 'Grandfather' : 'Grandmother';
    }

    // Sibling (same mother or same father)
    if (me.motherId && member.motherId === me.motherId && member.id !== me.id) return member.gender === 'male' ? 'Brother' : 'Sister';
    if (me.fatherId && member.fatherId === me.fatherId && member.id !== me.id) return member.gender === 'male' ? 'Brother' : 'Sister';

    // Aunt/Uncle (parent's sibling)
    if (myMother) {
      if (member.motherId === myMother.motherId || member.fatherId === myMother.motherId || member.motherId === myMother.fatherId || member.fatherId === myMother.fatherId) {
        if (member.id !== myMother.id && member.id !== myFather?.id) return member.gender === 'male' ? 'Uncle' : 'Aunt';
      }
    }
    if (myFather) {
      if (member.motherId === myFather.motherId || member.fatherId === myFather.motherId || member.motherId === myFather.fatherId || member.fatherId === myFather.fatherId) {
        if (member.id !== myFather.id && member.id !== myMother?.id) return member.gender === 'male' ? 'Uncle' : 'Aunt';
      }
    }

    // Grandchild (my child's children)
    const myChildren = rawMembers.filter(m => m.motherId === me.id || m.fatherId === me.id);
    for (const child of myChildren) {
      if (member.motherId === child.id || member.fatherId === child.id) return member.gender === 'male' ? 'Grandson' : 'Granddaughter';
    }

    // Cousin (my parent's sibling's children)
    const myParentSiblings = rawMembers.filter(m => {
      if (!myMother && !myFather) return false;
      const isParentSibling =
        (myMother && (m.motherId === myMother.motherId || m.fatherId === myMother.motherId || m.motherId === myMother.fatherId || m.fatherId === myMother.fatherId) && m.id !== myMother.id) ||
        (myFather && (m.motherId === myFather.motherId || m.fatherId === myFather.motherId || m.motherId === myFather.fatherId || m.fatherId === myFather.fatherId) && m.id !== myFather.id);
      return isParentSibling;
    });
    for (const uncle of myParentSiblings) {
      if (member.motherId === uncle.id || member.fatherId === uncle.id) return member.gender === 'male' ? 'Cousin' : 'Cousin';
    }

    // In-law (my spouse's parents/siblings)
    const mySpouse = memberMap[me.spouseId];
    if (mySpouse) {
      if (member.id === mySpouse.motherId || member.id === mySpouse.fatherId) return member.gender === 'male' ? 'Father-in-law' : 'Mother-in-law';
      if (member.motherId === mySpouse.motherId || member.fatherId === mySpouse.motherId || member.motherId === mySpouse.fatherId || member.fatherId === mySpouse.fatherId) {
        if (member.id !== mySpouse.id) return member.gender === 'male' ? 'Brother-in-law' : 'Sister-in-law';
      }
    }

    return null;
  }, [myId, memberMap]);

  // Fetch single member detail
  const [fetchMember, { data: memberData, loading: memberLoading }] = useLazyQuery(GET_MEMBER, {
    fetchPolicy: 'network-only',
  });

  const selectedMember = memberData?.member || null;

  const handleSelect = useCallback((id) => {
    setSelectedId(id);
    setShowDetail(true);
    setActiveTab('profile');
    fetchMember({ variables: { id } });
  }, [fetchMember]);

  const handleBack = () => {
    setShowDetail(false);
  };

  const handleMemberCreated = useCallback((newId) => {
    setShowAddMember(false);
    if (newId) {
      setSelectedId(newId);
      setShowDetail(true);
      setActiveTab('profile');
      fetchMember({ variables: { id: newId } });
    }
    fetchMembers({
      variables: { offset: 0, limit: PAGE_SIZE, ...searchParts },
    });
  }, [fetchMember, fetchMembers, searchParts]);

  const handleInviteSent = useCallback(() => {
    setShowInvite(false);
  }, []);

  return (
    <div className="h-full flex flex-col md:flex-row overflow-hidden">
      {/* Column 1 — Member List */}
      <div className={`${showDetail ? 'hidden md:flex' : 'flex'} w-full md:w-72 md:min-w-[280px] flex-col border-r border-gray-200 bg-white`}>
        {/* Search header */}
        <div className="sticky top-0 z-10 bg-white border-b border-gray-200 px-3 py-2">
          <div className="relative">
            <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
            <input
              type="text"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              placeholder="Search members..."
              className="w-full pl-8 pr-3 py-2 text-sm border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
            />
          </div>
          <div className="mt-2 flex items-center justify-between">
            <span className="text-xs text-gray-500">{total} member{total !== 1 ? 's' : ''}</span>
            <button
              onClick={() => setShowAddMember(true)}
              className="flex items-center gap-1 px-2 py-1 text-xs font-medium text-white bg-indigo-600 rounded-md hover:bg-indigo-700 transition-colors"
            >
              <Plus size={12} /> Add
            </button>
          </div>
        </div>

        {/* Member list */}
        <div className="flex-1 overflow-y-auto p-2 space-y-0.5">
          {membersLoading && !members.length ? (
            Array.from({ length: 5 }).map((_, i) => (
              <div key={i} className="rounded-lg px-3 py-2.5 animate-pulse">
                <div className="flex items-center gap-3">
                  <div className="w-9 h-9 rounded-full bg-gray-200" />
                  <div className="flex-1 space-y-1.5">
                    <div className="h-3 bg-gray-200 rounded w-24" />
                    <div className="h-2.5 bg-gray-200 rounded w-16" />
                  </div>
                </div>
              </div>
            ))
          ) : members.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-16 text-gray-400">
              <User className="w-10 h-10 mb-3 opacity-40" />
              <p className="text-sm font-medium">No members found</p>
              {debouncedSearch && (
                <p className="text-xs mt-1">Try a different search</p>
              )}
            </div>
          ) : (
            members.map((member) => {
              const isActive = selectedId === member.id;
              const isMe = member.id === myId;
              const initials = `${member.firstName?.[0] || ''}${member.lastName?.[0] || ''}`.toUpperCase();
              return (
                <button
                  key={member.id}
                  onClick={() => handleSelect(member.id)}
                  className={`w-full text-left rounded-lg px-3 py-2.5 transition-colors flex items-center gap-3 ${
                    isActive
                      ? 'bg-indigo-50 ring-1 ring-indigo-200'
                      : isMe
                        ? 'bg-amber-50 hover:bg-amber-100'
                        : 'hover:bg-gray-50'
                  }`}
                >
                  <div className={`w-9 h-9 rounded-full flex items-center justify-center text-xs font-bold flex-shrink-0 ${
                    member.gender === 'male'
                      ? 'bg-blue-100 text-blue-700'
                      : 'bg-pink-100 text-pink-700'
                  }`}>
                    {initials}
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium text-gray-900 truncate">
                      {member.firstName}{member.middleName ? " " + member.middleName : ""}{member.lastName ? " " + member.lastName[0] + "." : ""}
                      {isMe && <span className="ml-1.5 inline-flex items-center px-1.5 py-0.5 rounded-full text-[10px] font-semibold bg-amber-100 text-amber-700">You</span>}
                    </p>
                    <p className="text-xs text-gray-500 truncate">
                      {(() => {
                        const parts = [];
                        const rel = getRelationToMe(member);
                        if (rel) parts.push(rel);
                        parts.push(member.gender === 'male' ? 'Male' : member.gender === 'female' ? 'Female' : member.gender);
                        if (member.age > 0) parts.push(`${member.age} yrs`);
                        if (!member.isLiving) parts.push('Deceased');
                        return parts.join(' · ');
                      })()}
                    </p>
                  </div>
                </button>
              );
            })
          )}
        </div>

        {/* Pagination */}
        {totalPages > 1 && (
          <div className="border-t border-gray-200 px-3 py-2 flex items-center justify-between bg-white">
            <button
              onClick={() => setOffset(Math.max(0, offset - PAGE_SIZE))}
              disabled={offset === 0}
              className="p-1.5 rounded-lg text-gray-500 hover:bg-gray-100 disabled:opacity-30 disabled:cursor-not-allowed transition-colors"
            >
              <ChevronLeft size={16} />
            </button>
            <span className="text-xs text-gray-500">
              {currentPage} / {totalPages}
            </span>
            <button
              onClick={() => setOffset(Math.min((totalPages - 1) * PAGE_SIZE, offset + PAGE_SIZE))}
              disabled={currentPage >= totalPages}
              className="p-1.5 rounded-lg text-gray-500 hover:bg-gray-100 disabled:opacity-30 disabled:cursor-not-allowed transition-colors"
            >
              <ChevronRight size={16} />
            </button>
          </div>
        )}
      </div>

      {/* Column 2 — Profile Summary (desktop only) */}
      {selectedMember && (
        <div className="hidden md:flex w-72 md:min-w-[280px] flex-col border-r border-gray-200 bg-white overflow-y-auto">
          {/* Profile card */}
          <div className="p-4 border-b border-gray-200">
            <div className="flex flex-col items-center">
              <div className={`w-16 h-16 rounded-full flex items-center justify-center text-xl font-bold ${
                selectedMember.gender === 'male'
                  ? 'bg-blue-100 text-blue-700'
                  : 'bg-pink-100 text-pink-700'
              }`}>
                {`${selectedMember.firstName?.[0] || ''}${selectedMember.lastName?.[0] || ''}`.toUpperCase()}
              </div>
              <h2 className="mt-3 text-base font-semibold text-gray-900 text-center">
                {selectedMember.firstName}{selectedMember.middleName ? " " + selectedMember.middleName : ""} {selectedMember.lastName}
                {selectedMember.id === myId && <span className="ml-1.5 inline-flex items-center px-2 py-0.5 rounded-full text-[11px] font-semibold bg-amber-100 text-amber-700">You</span>}
              </h2>
              {selectedMember.spiritualName && (
                <p className="text-xs text-gray-500 italic">{selectedMember.spiritualName}</p>
              )}
              {selectedMember.adultName && selectedMember.adultName !== selectedMember.spiritualName && (
                <p className="text-xs text-gray-500">{selectedMember.adultName}</p>
              )}
              <div className="flex items-center gap-2 mt-2 flex-wrap justify-center">
                <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-[11px] font-medium ${
                  selectedMember.gender === 'male' ? 'bg-blue-100 text-blue-700' : 'bg-pink-100 text-pink-700'
                }`}>
                  {selectedMember.gender === 'male' ? 'Male' : selectedMember.gender === 'female' ? 'Female' : selectedMember.gender}
                </span>
                {selectedMember.age > 0 && (
                  <span className="inline-flex items-center px-2 py-0.5 rounded-full text-[11px] font-medium bg-gray-100 text-gray-600">
                    {selectedMember.age} years old
                  </span>
                )}
                {!selectedMember.isLiving && (
                  <span className="inline-flex items-center px-2 py-0.5 rounded-full text-[11px] font-medium bg-gray-200 text-gray-600">
                    Deceased
                  </span>
                )}
              </div>
            </div>
          </div>

          {/* Quick info */}
          <div className="p-4 space-y-3 flex-1">
            {selectedMember.birthDate && (
              <div className="flex items-start gap-2.5">
                <AppIcon name="cake" size={16} className="text-gray-400 mt-0.5 flex-shrink-0" />
                <div>
                  <p className="text-xs text-gray-500">Born</p>
                  <p className="text-sm text-gray-900">{formatDate(selectedMember.birthDate)}</p>
                </div>
              </div>
            )}
            {selectedMember.birthPlace && (
              <div className="flex items-start gap-2.5">
                <AppIcon name="globe" size={16} className="text-gray-400 mt-0.5 flex-shrink-0" />
                <div>
                  <p className="text-xs text-gray-500">Birthplace</p>
                  <p className="text-sm text-gray-900">{selectedMember.birthPlace}</p>
                </div>
              </div>
            )}
            {selectedMember.deathDate && (
              <div className="flex items-start gap-2.5">
                <AppIcon name="shield" size={16} className="text-gray-400 mt-0.5 flex-shrink-0" />
                <div>
                  <p className="text-xs text-gray-500">Passed</p>
                  <p className="text-sm text-gray-900">{formatDate(selectedMember.deathDate)}</p>
                </div>
              </div>
            )}
            {selectedMember.deathPlace && (
              <div className="flex items-start gap-2.5">
                <AppIcon name="globe" size={16} className="text-gray-400 mt-0.5 flex-shrink-0" />
                <div>
                  <p className="text-xs text-gray-500">Place of death</p>
                  <p className="text-sm text-gray-900">{selectedMember.deathPlace}</p>
                </div>
              </div>
            )}
            {selectedMember.birthLat && selectedMember.birthLng && (
              <div className="flex items-start gap-2.5">
                <AppIcon name="search" size={16} className="text-gray-400 mt-0.5 flex-shrink-0" />
                <div>
                  <p className="text-xs text-gray-500">Birth coordinates</p>
                  <p className="text-sm text-gray-900">{Number(selectedMember.birthLat).toFixed(4)}°, {Number(selectedMember.birthLng).toFixed(4)}°</p>
                </div>
              </div>
            )}
            {selectedMember.deathLat && selectedMember.deathLng && (
              <div className="flex items-start gap-2.5">
                <AppIcon name="search" size={16} className="text-gray-400 mt-0.5 flex-shrink-0" />
                <div>
                  <p className="text-xs text-gray-500">Death coordinates</p>
                  <p className="text-sm text-gray-900">{Number(selectedMember.deathLat).toFixed(4)}°, {Number(selectedMember.deathLng).toFixed(4)}°</p>
                </div>
              </div>
            )}
          </div>

          {/* Invite button */}
          <div className="px-4 pb-4">
            <button
              onClick={() => setShowInvite(true)}
              className="w-full flex items-center justify-center gap-2 px-3 py-2 text-sm font-medium text-indigo-700 bg-indigo-50 rounded-lg hover:bg-indigo-100 transition-colors border border-indigo-200"
            >
              <Mail size={16} />
              Invite to Family Tree
            </button>
          </div>
        </div>
      )}

      {/* Column 3 — Detail with tabs */}
      <div className={`${showDetail ? 'flex' : 'hidden md:flex'} flex-1 min-h-0 flex-col bg-gray-50`}>
        {/* Mobile back button */}
        {showDetail && (
          <div className="md:hidden pt-4 pb-3 px-3 border-b border-gray-200 bg-white">
            <button
              onClick={handleBack}
              className="flex items-center gap-2 text-sm text-gray-600 hover:text-gray-900"
            >
              <ChevronLeft size={16} />
              Back to list
            </button>
          </div>
        )}

        {selectedMember ? (
          <>
            {/* Mobile-only header (desktop shows it in column 2) */}
            <div className="md:hidden bg-white border-b border-gray-200 px-4 py-3">
              <div className="flex items-center gap-3">
                <div className={`w-11 h-11 rounded-full flex items-center justify-center text-sm font-bold flex-shrink-0 ${
                  selectedMember.gender === 'male'
                    ? 'bg-blue-100 text-blue-700'
                    : 'bg-pink-100 text-pink-700'
                }`}>
                  {`${selectedMember.firstName?.[0] || ''}${selectedMember.lastName?.[0] || ''}`.toUpperCase()}
                </div>
                <div className="flex-1 min-w-0">
                  <h2 className="text-base font-semibold text-gray-900 truncate">
                    {selectedMember.firstName}{selectedMember.middleName ? " " + selectedMember.middleName : ""} {selectedMember.lastName}
                  </h2>
                  <div className="flex items-center gap-2 mt-0.5">
                    <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-[11px] font-medium ${
                      selectedMember.gender === 'male' ? 'bg-blue-100 text-blue-700' : 'bg-pink-100 text-pink-700'
                    }`}>
                      {selectedMember.gender === 'male' ? 'Male' : selectedMember.gender === 'female' ? 'Female' : selectedMember.gender}
                    </span>
                    {selectedMember.age > 0 && (
                      <span className="text-[11px] text-gray-500">{selectedMember.age} years old</span>
                    )}
                  </div>
                </div>
                {/* Mobile invite button */}
                <button
                  onClick={() => setShowInvite(true)}
                  className="p-2 text-indigo-600 hover:bg-indigo-50 rounded-lg transition-colors"
                  title="Invite"
                >
                  <Mail size={20} />
                </button>
              </div>
            </div>

            {/* Tabs */}
            <div className="flex items-center border-b border-gray-200 bg-white px-4 py-1">
              <button
                onClick={() => setActiveTab('profile')}
                className={`flex items-center gap-1.5 px-3 py-2 text-sm font-medium transition-colors border-b-2 -mb-px ${
                  activeTab === 'profile'
                    ? 'border-indigo-600 text-indigo-600'
                    : 'border-transparent text-gray-500 hover:text-gray-700'
                }`}
              >
                <UserCircle size={16} />
                Profile
              </button>
              <button
                onClick={() => setActiveTab('notes')}
                className={`flex items-center gap-1.5 px-3 py-2 text-sm font-medium transition-colors border-b-2 -mb-px ${
                  activeTab === 'notes'
                    ? 'border-indigo-600 text-indigo-600'
                    : 'border-transparent text-gray-500 hover:text-gray-700'
                }`}
              >
                <FileText size={16} />
                Notes
              </button>
            </div>

            {/* Tab Content */}
            <div className="flex-1 min-h-0 overflow-auto px-4 py-4 md:px-6">
              {activeTab === 'profile' && (
                <ProfileTab member={selectedMember} members={rawMembers} onUpdate={() => { fetchMember({ variables: { id: selectedId } }); fetchMembers({ variables: { offset: 0, limit: PAGE_SIZE } }); }} onClose={() => { setSelectedId(null); setShowDetail(false); }} />
              )}
              {activeTab === 'notes' && (
                <NotesTab member={selectedMember} />
              )}
            </div>
          </>
        ) : (
          <div className="flex-1 flex items-center justify-center text-gray-400">
            <div className="text-center">
              <User className="w-12 h-12 mx-auto mb-3 opacity-30" />
              <p className="text-sm font-medium">Select a member</p>
              <p className="text-xs mt-1">Choose from the list to view details</p>
            </div>
          </div>
        )}
      </div>

      {/* Add Member Modal */}
      {showAddMember && (
        <AddMemberModal
          onClose={() => setShowAddMember(false)}
          onCreated={handleMemberCreated}
          existingMembers={members}
        />
      )}

      {/* Invite Modal */}
      {showInvite && selectedMember && (
        <InviteModal
          member={selectedMember}
          inviterId={getPersonId()}
          onClose={() => setShowInvite(false)}
          onSent={handleInviteSent}
        />
      )}
    </div>
  );
}

function formatDate(dateStr) {
  if (!dateStr) return '—';
  try {
    const d = new Date(dateStr + 'T00:00:00');
    return d.toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' });
  } catch {
    return dateStr;
  }
}

function ProfileTab({ member, members, onUpdate, onClose }) {
  const { data: allMembersData } = useQuery(GET_ALL_MEMBERS, { fetchPolicy: 'cache-and-network' });
  const allMembers = allMembersData?.members?.members || members || [];
  const [editing, setEditing] = useState(false);
  const [form, setForm] = useState({});
  const [saving, setSaving] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [updateMember] = useMutation(UPDATE_MEMBER);
  const [deleteMember] = useMutation(DELETE_MEMBER);

  useEffect(() => {
    if (editing) {
      setForm({
        firstName: member.firstName || '',
        middleName: member.middleName || '',
        lastName: member.lastName || '',
        gender: member.gender || 'male',
        birthDate: member.birthDate || '',
        birthPlace: member.birthPlace || '',
        birthLat: member.birthLat ?? '',
        birthLng: member.birthLng ?? '',
        deathDate: member.deathDate || '',
        deathPlace: member.deathPlace || '',
        deathLat: member.deathLat ?? '',
        deathLng: member.deathLng ?? '',
        spiritualName: member.spiritualName || '',
        adultName: member.adultName || '',
        isLiving: member.isLiving !== false,
        motherId: member.motherId || '',
        fatherId: member.fatherId || '',
        spouseId: member.spouseId || '',
        stepFatherId: (() => {
          if (!member.motherId) return '';
          const mother = (allMembers || []).find(m => m.id === member.motherId);
          if (mother?.spouseId && mother.spouseId !== member.fatherId) return mother.spouseId;
          return '';
        })(),
        stepMotherId: (() => {
          if (!member.fatherId) return '';
          const father = (allMembers || []).find(m => m.id === member.fatherId);
          if (father?.spouseId && father.spouseId !== member.motherId) return father.spouseId;
          return '';
        })(),
      });
    }
  }, [editing, member.id]);

  const handleChange = (field, value) => {
    setForm(prev => ({ ...prev, [field]: value }));
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      const input = {};
      const stepParentUpdates = [];
      if (form.firstName !== (member.firstName || '')) input.firstName = form.firstName;
      if (form.middleName !== (member.middleName || '')) input.middleName = form.middleName;
      if (form.lastName !== (member.lastName || '')) input.lastName = form.lastName;
      if (form.gender !== (member.gender || 'male')) input.gender = form.gender;
      if (form.birthDate !== (member.birthDate || '')) input.birthDate = form.birthDate || '';
      if (form.birthPlace !== (member.birthPlace || '')) input.birthPlace = form.birthPlace;
      if (form.birthLat !== (member.birthLat ?? '')) input.birthLat = form.birthLat ? parseFloat(form.birthLat) : null;
      if (form.birthLng !== (member.birthLng ?? '')) input.birthLng = form.birthLng ? parseFloat(form.birthLng) : null;
      if (form.deathDate !== (member.deathDate || '')) input.deathDate = form.deathDate || '';
      if (form.deathPlace !== (member.deathPlace || '')) input.deathPlace = form.deathPlace;
      if (form.deathLat !== (member.deathLat ?? '')) input.deathLat = form.deathLat ? parseFloat(form.deathLat) : null;
      if (form.deathLng !== (member.deathLng ?? '')) input.deathLng = form.deathLng ? parseFloat(form.deathLng) : null;
      if (form.spiritualName !== (member.spiritualName || '')) input.spiritualName = form.spiritualName;
      if (form.adultName !== (member.adultName || '')) input.adultName = form.adultName;
      if (form.isLiving !== member.isLiving) input.isLiving = form.isLiving;
      if (form.motherId !== (member.motherId || '')) input.motherId = form.motherId;
      if (form.fatherId !== (member.fatherId || '')) input.fatherId = form.fatherId;
      if (form.spouseId !== (member.spouseId || '')) input.spouseId = form.spouseId;

      // Step-parents: update the parent's spouseId
      const origStepFatherId = (() => {
        if (!member.motherId) return '';
        const mother = (allMembers || []).find(m => m.id === member.motherId);
        if (mother?.spouseId && mother.spouseId !== member.fatherId) return mother.spouseId;
        return '';
      })();
      const origStepMotherId = (() => {
        if (!member.fatherId) return '';
        const father = (allMembers || []).find(m => m.id === member.fatherId);
        if (father?.spouseId && father.spouseId !== member.motherId) return father.spouseId;
        return '';
      })();

      if (form.stepFatherId !== origStepFatherId && member.motherId) {
        // Update mother's spouseId
        stepParentUpdates.push({ id: member.motherId, spouseId: form.stepFatherId || '' });
      }
      if (form.stepMotherId !== origStepMotherId && member.fatherId) {
        // Update father's spouseId
        stepParentUpdates.push({ id: member.fatherId, spouseId: form.stepMotherId || '' });
      }

      // Only save if there are changes to the main member OR step-parent updates
      if (Object.keys(input).length === 0 && stepParentUpdates.length === 0) {
        setEditing(false);
        setSaving(false);
        return;
      }

      if (Object.keys(input).length > 0) {
        await updateMember({ variables: { id: member.id, input } });
      }

      // Apply step-parent updates (set spouseId on mother/father)
      for (const spu of stepParentUpdates) {
        try {
          const updateInput = {};
          if (spu.spouseId !== undefined) updateInput.spouseId = spu.spouseId || '';
          await updateMember({ variables: { id: spu.id, input: updateInput } });
        } catch (err) {
          console.error('Failed to update step-parent:', err);
        }
      }

      setEditing(false);
      if (onUpdate) onUpdate();
    } catch (err) {
      console.error('Failed to update member:', err);
    } finally {
      setSaving(false);
    }
  };

  const handleCancel = () => {
    setEditing(false);
  };

  const handleDelete = async () => {
    setDeleting(true);
    try {
      await deleteMember({
        variables: { id: String(member.id) },
        refetchQueries: [{ query: GET_MEMBERS, variables: { offset: 0, limit: PAGE_SIZE } }, { query: GET_ALL_MEMBERS }],
      });
      setShowDeleteConfirm(false);
      if (onUpdate) onUpdate();
      if (onClose) onClose();
    } catch (err) {
      console.error('Failed to delete member:', err);
      alert('Failed to delete member: ' + (err.message || 'Unknown error'));
    } finally {
      setDeleting(false);
    }
  };

  const details = [
    { label: 'ID', value: member.id },
    { label: 'First Name', value: member.firstName },
    { label: 'Middle Name', value: member.middleName },
    { label: 'Last Name', value: member.lastName },
    { label: 'Gender', value: member.gender === 'male' ? 'Male' : member.gender === 'female' ? 'Female' : member.gender },
    { label: 'Date of Birth', value: member.birthDate ? formatDate(member.birthDate) : null },
    { label: 'Place of Birth', value: member.birthPlace },
    { label: 'Date of Death', value: member.deathDate ? formatDate(member.deathDate) : (member.isLiving ? null : 'Unknown') },
    { label: 'Place of Death', value: member.deathPlace },
    { label: 'Spiritual Name', value: member.spiritualName },
    { label: 'Adult Name', value: member.adultName },
    { label: 'Age', value: member.age > 0 ? `${member.age} years` : null },
    { label: 'Status', value: member.isLiving ? 'Living' : 'Deceased' },
  ];

  if (editing) {
    return (
      <div className="space-y-6">
        <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-5">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-sm font-semibold text-gray-900">Edit Profile</h3>
            <div className="flex gap-2">
              <button onClick={handleCancel} className="px-3 py-1.5 text-xs font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors">Cancel</button>
              <button onClick={handleSave} disabled={saving} className="px-3 py-1.5 text-xs font-medium text-white bg-indigo-600 rounded-lg hover:bg-indigo-700 disabled:opacity-50 transition-colors">
                {saving ? 'Saving...' : 'Save'}
              </button>
            </div>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <label className="block text-xs font-medium text-gray-500 mb-1">ID</label>
              <input type="text" value={member.id} readOnly
                className="w-full px-3 py-1.5 text-sm border border-gray-200 rounded-lg bg-gray-50 text-gray-500 cursor-not-allowed" />
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-500 mb-1">First Name</label>
              <input type="text" value={form.firstName} onChange={(e) => handleChange('firstName', e.target.value)}
                className="w-full px-3 py-1.5 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500" />
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-500 mb-1">Middle Name</label>
              <input type="text" value={form.middleName} onChange={(e) => handleChange('middleName', e.target.value)}
                className="w-full px-3 py-1.5 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500" />
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-500 mb-1">Last Name</label>
              <input type="text" value={form.lastName} onChange={(e) => handleChange('lastName', e.target.value)}
                className="w-full px-3 py-1.5 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500" />
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-500 mb-1">Gender</label>
              <select value={form.gender} onChange={(e) => handleChange('gender', e.target.value)}
                className="w-full px-3 py-1.5 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500">
                <option value="male">Male</option>
                <option value="female">Female</option>
                <option value="other">Other</option>
              </select>
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-500 mb-1">Date of Birth</label>
              <input type="date" value={form.birthDate} onChange={(e) => handleChange('birthDate', e.target.value)}
                className="w-full px-3 py-1.5 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500" />
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-500 mb-1">Place of Birth</label>
              <input type="text" value={form.birthPlace} onChange={(e) => handleChange('birthPlace', e.target.value)}
                className="w-full px-3 py-1.5 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500" />
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-500 mb-1">Spiritual Name</label>
              <input type="text" value={form.spiritualName} onChange={(e) => handleChange('spiritualName', e.target.value)}
                className="w-full px-3 py-1.5 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500" />
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-500 mb-1">Adult Name</label>
              <input type="text" value={form.adultName} onChange={(e) => handleChange('adultName', e.target.value)}
                className="w-full px-3 py-1.5 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500" />
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-500 mb-1">Date of Death</label>
              <input type="date" value={form.deathDate} onChange={(e) => handleChange('deathDate', e.target.value)}
                className="w-full px-3 py-1.5 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500" />
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-500 mb-1">Place of Death</label>
              <input type="text" value={form.deathPlace} onChange={(e) => handleChange('deathPlace', e.target.value)}
                className="w-full px-3 py-1.5 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500" />
            </div>
            <div className="col-span-1 sm:col-span-2">
              <LocationPicker
                lat={form.birthLat ? parseFloat(form.birthLat) : null}
                lng={form.birthLng ? parseFloat(form.birthLng) : null}
                onChange={(lat, lng) => { handleChange('birthLat', lat); handleChange('birthLng', lng); }}
                label="Birth Location"
              />
            </div>
            {form.deathPlace && (
              <div className="col-span-1 sm:col-span-2">
                <LocationPicker
                  lat={form.deathLat ? parseFloat(form.deathLat) : null}
                  lng={form.deathLng ? parseFloat(form.deathLng) : null}
                  onChange={(lat, lng) => { handleChange('deathLat', lat); handleChange('deathLng', lng); }}
                  label="Death Location"
                />
              </div>
            )}
            <div className="flex items-center gap-2 col-span-1 sm:col-span-2">
              <input type="checkbox" id="editIsLiving" checked={form.isLiving} onChange={(e) => handleChange('isLiving', e.target.checked)}
                className="w-4 h-4 text-indigo-600 border-gray-300 rounded focus:ring-indigo-500" />
              <label htmlFor="editIsLiving" className="text-sm text-gray-700">Living</label>
            </div>
          </div>
        </div>

        {/* Relationships Section */}
        <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-5">
          <h3 className="text-sm font-semibold text-gray-900 mb-4">Relationships</h3>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
            <div>
              <div className="flex items-center justify-between mb-1">
                <label className="text-xs font-medium text-gray-500">Mother</label>
                {form.motherId && (
                  <button type="button" onClick={() => handleChange('motherId', '')} className="text-xs text-red-500 hover:text-red-700">Clear</button>
                )}
              </div>
              <select value={form.motherId} onChange={(e) => handleChange('motherId', e.target.value)}
                className="w-full px-3 py-1.5 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500">
                <option value="">None</option>
                {(allMembers || []).filter(m => m.gender === 'female' && m.id !== member.id).map(m => (
                  <option key={m.id} value={m.id}>{m.firstName}{m.middleName ? " " + m.middleName : ""} {m.lastName}</option>
                ))}
              </select>
            </div>
            <div>
              <div className="flex items-center justify-between mb-1">
                <label className="text-xs font-medium text-gray-500">Father</label>
                {form.fatherId && (
                  <button type="button" onClick={() => handleChange('fatherId', '')} className="text-xs text-red-500 hover:text-red-700">Clear</button>
                )}
              </div>
              <select value={form.fatherId} onChange={(e) => handleChange('fatherId', e.target.value)}
                className="w-full px-3 py-1.5 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500">
                <option value="">None</option>
                {(allMembers || []).filter(m => m.gender === 'male' && m.id !== member.id).map(m => (
                  <option key={m.id} value={m.id}>{m.firstName}{m.middleName ? " " + m.middleName : ""} {m.lastName}</option>
                ))}
              </select>
            </div>
            <div>
              <div className="flex items-center justify-between mb-1">
                <label className="text-xs font-medium text-gray-500">Spouse</label>
                {form.spouseId && (
                  <button type="button" onClick={() => handleChange('spouseId', '')} className="text-xs text-red-500 hover:text-red-700">Clear</button>
                )}
              </div>
              <select value={form.spouseId} onChange={(e) => handleChange('spouseId', e.target.value)}
                className="w-full px-3 py-1.5 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500">
                <option value="">None</option>
                {(allMembers || []).filter(m => m.id !== member.id).map(m => (
                  <option key={m.id} value={m.id}>{m.firstName}{m.middleName ? " " + m.middleName : ""} {m.lastName}</option>
                ))}
              </select>
            </div>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mt-4">
            <div>
              <div className="flex items-center justify-between mb-1">
                <label className="text-xs font-medium text-gray-500">Step-Father</label>
                {form.stepFatherId && (
                  <button type="button" onClick={() => handleChange('stepFatherId', '')} className="text-xs text-red-500 hover:text-red-700">Clear</button>
                )}
              </div>
              <select value={form.stepFatherId || ''} onChange={(e) => handleChange('stepFatherId', e.target.value)}
                className="w-full px-3 py-1.5 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500">
                <option value="">None</option>
                {(allMembers || []).filter(m => m.gender === 'male' && m.id !== member.id).map(m => (
                  <option key={m.id} value={m.id}>{m.firstName}{m.middleName ? " " + m.middleName : ""} {m.lastName}</option>
                ))}
              </select>
              <p className="text-[10px] text-gray-400 mt-1">Spouse of your mother</p>
            </div>
            <div>
              <div className="flex items-center justify-between mb-1">
                <label className="text-xs font-medium text-gray-500">Step-Mother</label>
                {form.stepMotherId && (
                  <button type="button" onClick={() => handleChange('stepMotherId', '')} className="text-xs text-red-500 hover:text-red-700">Clear</button>
                )}
              </div>
              <select value={form.stepMotherId || ''} onChange={(e) => handleChange('stepMotherId', e.target.value)}
                className="w-full px-3 py-1.5 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500">
                <option value="">None</option>
                {(allMembers || []).filter(m => m.gender === 'female' && m.id !== member.id).map(m => (
                  <option key={m.id} value={m.id}>{m.firstName}{m.middleName ? " " + m.middleName : ""} {m.lastName}</option>
                ))}
              </select>
              <p className="text-[10px] text-gray-400 mt-1">Spouse of your father</p>
            </div>
          </div>

        </div>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-5">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-sm font-semibold text-gray-900">Personal Information</h3>
          <div className="flex gap-2">
            <button onClick={() => setEditing(true)} className="px-3 py-1.5 text-xs font-medium text-indigo-700 bg-indigo-50 rounded-lg hover:bg-indigo-100 transition-colors border border-indigo-200">
              Edit
            </button>
            <button onClick={() => setShowDeleteConfirm(true)} className="px-3 py-1.5 text-xs font-medium text-red-700 bg-red-50 rounded-lg hover:bg-red-100 transition-colors border border-red-200">
              Delete
            </button>
          </div>
        </div>
        <dl className="space-y-3">
          {details.map(({ label, value }) => (
            value && (
              <div key={label} className="flex items-start gap-4">
                <dt className="text-xs text-gray-500 w-28 flex-shrink-0 pt-0.5">{label}</dt>
                <dd className="text-sm text-gray-900 flex-1">{value}</dd>
              </div>
            )
          ))}
        </dl>
      </div>

      {/* Delete Confirmation Modal */}
      {showDeleteConfirm && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50" onClick={() => setShowDeleteConfirm(false)}>
          <div className="bg-white rounded-xl shadow-xl p-6 max-w-sm w-full mx-4" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center gap-3 mb-4">
              <div className="w-10 h-10 rounded-full bg-red-100 flex items-center justify-center">
                <svg className="w-5 h-5 text-red-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L4.082 16.5c-.77.833.192 2.5 1.732 2.5z" />
                </svg>
              </div>
              <div>
                <h3 className="text-lg font-semibold text-gray-900">Delete Member</h3>
                <p className="text-sm text-gray-500">This action cannot be undone.</p>
              </div>
            </div>
            <p className="text-sm text-gray-700 mb-4">
              Are you sure you want to delete <strong>{member.firstName}{member.middleName ? ' ' + member.middleName : ''} {member.lastName}</strong>?
              Any family connections (parent, child, spouse) will be removed.
            </p>
            <div className="flex gap-3 justify-end">
              <button
                onClick={() => setShowDeleteConfirm(false)}
                className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
                disabled={deleting}
              >
                Cancel
              </button>
              <button
                onClick={handleDelete}
                className="px-4 py-2 text-sm font-medium text-white bg-red-600 rounded-lg hover:bg-red-700 transition-colors disabled:opacity-50"
                disabled={deleting}
              >
                {deleting ? 'Deleting...' : 'Delete'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function NotesTab({ member }) {
  const [notes, setNotes] = useState(member.notes || '');
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);
  const [updateMember] = useMutation(UPDATE_MEMBER);
  const textareaRef = useRef(null);

  useEffect(() => {
    setNotes(member.notes || '');
    setSaved(false);
  }, [member.id, member.notes]);

  const handleSave = async () => {
    setSaving(true);
    try {
      await updateMember({
        variables: { id: member.id, input: { notes } },
      });
      setSaved(true);
      setTimeout(() => setSaved(false), 2000);
    } catch (err) {
      console.error('Failed to save notes:', err);
    } finally {
      setSaving(false);
    }
  };

  const handleKeyDown = (e) => {
    if ((e.metaKey || e.ctrlKey) && e.key === 's') {
      e.preventDefault();
      handleSave();
    }
  };

  return (
    <div className="flex flex-col h-full">
      <div className="flex items-center justify-between mb-2">
        <h3 className="text-sm font-semibold text-gray-900">Notes</h3>
        <div className="flex items-center gap-2">
          {saved && <span className="text-xs text-green-600 font-medium">Saved</span>}
          <button
            onClick={handleSave}
            disabled={saving}
            className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium text-white bg-indigo-600 rounded-lg hover:bg-indigo-700 disabled:opacity-50 transition-colors"
          >
            <Save size={14} />
            {saving ? 'Saving...' : 'Save'}
          </button>
        </div>
      </div>
      <textarea
        ref={textareaRef}
        value={notes}
        onChange={(e) => { setNotes(e.target.value); setSaved(false); }}
        onKeyDown={handleKeyDown}
        placeholder="Add notes about this member..."
        className="flex-1 min-h-[200px] w-full p-4 text-sm border border-gray-200 rounded-xl bg-white focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 resize-none"
      />
      <p className="text-[11px] text-gray-400 mt-1.5">⌘S to save</p>
    </div>
  );
}

function AddMemberModal({ onClose, onCreated, existingMembers }) {
  const [updateExistingMember] = useMutation(UPDATE_MEMBER);
  const [form, setForm] = useState({
    firstName: '',
    middleName: '',
    lastName: '',
    gender: 'male',
    birthDate: '',
    birthPlace: '',
    spiritualName: '',
    adultName: '',
    isLiving: true,
  });
  const [relationship, setRelationship] = useState({ relatedTo: '', relationType: '' });
  const [errors, setErrors] = useState({});
  const [creating, setCreating] = useState(false);
  const [createMember] = useMutation(CREATE_MEMBER);

  const handleChange = (field, value) => {
    setForm((prev) => ({ ...prev, [field]: value }));
    setErrors((prev) => ({ ...prev, [field]: undefined }));
  };

  // Relationship type options - simplified, auto-detects maternal/paternal based on current user's gender
  const RELATIONSHIP_OPTIONS = [
    { group: 'Parent', options: [
      { value: 'father', label: 'Father' },
      { value: 'mother', label: 'Mother' },
    ]},
    { group: 'Step-Parent', options: [
      { value: 'step_father', label: 'Step-Father' },
      { value: 'step_mother', label: 'Step-Mother' },
    ]},
    { group: 'Child', options: [
      { value: 'son', label: 'Son' },
      { value: 'daughter', label: 'Daughter' },
    ]},
    { group: 'Spouse', options: [
      { value: 'husband', label: 'Husband' },
      { value: 'wife', label: 'Wife' },
    ]},
    { group: 'Sibling', options: [
      { value: 'brother', label: 'Brother' },
      { value: 'sister', label: 'Sister' },
    ]},
  ];

  const handleSubmit = async (e) => {
    e.preventDefault();
    const newErrors = {};
    if (!form.firstName.trim()) newErrors.firstName = 'First name is required';
    if (!form.gender) newErrors.gender = 'Gender is required';
    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors);
      return;
    }

    setCreating(true);
    try {
      const input = {
        firstName: form.firstName.trim(),
        middleName: form.middleName.trim() || undefined,
        lastName: form.lastName.trim() || undefined,
        gender: form.gender,
        isLiving: form.isLiving,
      };
      if (form.spiritualName.trim()) input.spiritualName = form.spiritualName.trim();
      if (form.adultName.trim()) input.adultName = form.adultName.trim();
      if (form.birthDate) input.birthDate = form.birthDate;
      if (form.birthPlace.trim()) input.birthPlace = form.birthPlace.trim();

      // Set relationship-based IDs
      // The label says: "NewMember's relationship to ExistingMember"
      // Parent: new person IS the parent → update existing person to point to new parent
      // Child: new person IS the child → set new person's parent field to existing person
      // Auto-detect fatherId/motherId based on existing person's gender
      const relType = relationship.relationType;
      const relMemberId = relationship.relatedTo;
      const existingPerson = (existingMembers || []).find(m => m.id === relMemberId);
      const existingGender = existingPerson?.gender;
      let updateExistingInput = null;

      if (relType && relMemberId) {
        if (relType === 'father') {
          // New person is the father of existing person
          updateExistingInput = { id: relMemberId, fatherId: '__NEW_ID__' };
        }
        if (relType === 'mother') {
          // New person is the mother of existing person
          updateExistingInput = { id: relMemberId, motherId: '__NEW_ID__' };
        }
        if (relType === 'son' || relType === 'daughter') {
          // New person is a child of existing person
          // Auto-detect: if existing person is male → set fatherId, if female → set motherId
          if (existingGender === 'male') {
            input.fatherId = relMemberId;
          } else {
            input.motherId = relMemberId;
          }
        }
        if (relType === 'brother' || relType === 'sister') {
          // New person is a sibling of existing person → share the same parents
          if (existingPerson) {
            if (existingPerson.motherId) input.motherId = existingPerson.motherId;
            if (existingPerson.fatherId) input.fatherId = existingPerson.fatherId;
          }
        }
        if (relType === 'step_father') {
          // New person is the step-father = spouse of existing person's mother
          // Set new person as spouse of existing person's mother
          if (existingPerson?.motherId) {
            updateExistingInput = { id: existingPerson.motherId, spouseId: '__NEW_ID__' };
          }
        }
        if (relType === 'step_mother') {
          // New person is the step-mother = spouse of existing person's father
          // Set new person as spouse of existing person's father
          if (existingPerson?.fatherId) {
            updateExistingInput = { id: existingPerson.fatherId, spouseId: '__NEW_ID__' };
          }
        }
        if (relType === 'husband' || relType === 'wife') {
          input.spouseId = relMemberId;
        }
      }

      const result = await createMember({ variables: { input } });
      const newId = result.data?.createMember?.id;

      // If this was a child relationship, update the existing member to point to the new parent
      if (updateExistingInput && newId) {
        const updateInput = { ...updateExistingInput };
        // Replace placeholder with actual new ID
        for (const key of Object.keys(updateInput)) {
          if (updateInput[key] === '__NEW_ID__') {
            updateInput[key] = newId;
          }
        }
        delete updateInput.id;
        try {
          await updateExistingMember({ variables: { id: updateExistingInput.id, input: updateInput } });
        } catch (err) {
          console.error('Failed to update existing member relationship:', err);
        }
      }

      onCreated(newId);
    } catch (err) {
      console.error('Failed to create member:', err);
      setErrors({ submit: 'Failed to add member. Please try again.' });
    } finally {
      setCreating(false);
    }
  };

  // Filter members for the search dropdown
  const [memberSearch, setMemberSearch] = useState('');
  const filteredMembers = (existingMembers || []).filter(m => {
    if (!memberSearch.trim()) return true;
    const q = memberSearch.toLowerCase();
    return (m.firstName || '').toLowerCase().includes(q) || (m.lastName || '').toLowerCase().includes(q);
  }).slice(0, 20);

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40" onClick={onClose}>
      <div className="bg-white rounded-xl shadow-xl w-full max-w-md mx-4 max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between px-4 py-3 border-b border-gray-200">
          <h2 className="text-base font-semibold text-gray-900">Add Family Member</h2>
          <button onClick={onClose} className="p-1 text-gray-400 hover:text-gray-600"><X size={18} /></button>
        </div>
        <form onSubmit={handleSubmit} className="p-4 space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">First Name *</label>
            <input type="text" value={form.firstName} onChange={(e) => handleChange('firstName', e.target.value)}
              className={`w-full px-3 py-2 border rounded-lg text-sm focus:ring-2 focus:ring-indigo-500 ${errors.firstName ? 'border-red-300' : 'border-gray-300'}`} />
            {errors.firstName && <p className="text-xs text-red-600 mt-1">{errors.firstName}</p>}
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Middle Name</label>
            <input type="text" value={form.middleName} onChange={(e) => handleChange('middleName', e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-indigo-500" />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Last Name</label>
            <input type="text" value={form.lastName} onChange={(e) => handleChange('lastName', e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-indigo-500" />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Gender *</label>
            <div className="flex gap-2">
              {['male', 'female', 'other'].map((g) => (
                <button key={g} type="button" onClick={() => handleChange('gender', g)}
                  className={`flex-1 px-3 py-2 text-sm font-medium rounded-lg transition-colors ${
                    form.gender === g ? 'bg-indigo-600 text-white' : 'bg-white border border-gray-300 text-gray-600 hover:bg-gray-50'
                  }`}>
                  {g.charAt(0).toUpperCase() + g.slice(1)}
                </button>
              ))}
            </div>
            {errors.gender && <p className="text-xs text-red-600 mt-1">{errors.gender}</p>}
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Date of Birth</label>
            <input type="date" value={form.birthDate} onChange={(e) => handleChange('birthDate', e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-indigo-500" />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Place of Birth</label>
            <input type="text" value={form.birthPlace} onChange={(e) => handleChange('birthPlace', e.target.value)} placeholder="e.g. Sacramento, CA"
              className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-indigo-500" />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Spiritual Name</label>
            <input type="text" value={form.spiritualName} onChange={(e) => handleChange('spiritualName', e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-indigo-500" />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Adult Name</label>
            <input type="text" value={form.adultName} onChange={(e) => handleChange('adultName', e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-indigo-500" />
          </div>
          <div className="flex items-center gap-2">
            <input type="checkbox" id="isLiving" checked={form.isLiving} onChange={(e) => handleChange('isLiving', e.target.checked)}
              className="w-4 h-4 text-indigo-600 border-gray-300 rounded focus:ring-indigo-500" />
            <label htmlFor="isLiving" className="text-sm text-gray-700">Living</label>
          </div>

          {/* Relationship Section */}
          <div className="border-t border-gray-200 pt-4">
            <h3 className="text-sm font-semibold text-gray-900 mb-3">Relationship (Optional)</h3>
            <p className="text-xs text-gray-500 mb-3">Link this person to an existing family member to build your family tree.</p>

            {/* Related To */}
            <div className="mb-3">
              <label className="block text-sm font-medium text-gray-700 mb-1">Related to</label>
              <select
                value={relationship.relatedTo}
                onChange={(e) => setRelationship(prev => ({ ...prev, relatedTo: e.target.value }))}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-indigo-500"
              >
                <option value="">Select a family member...</option>
                {(existingMembers || []).map(m => (
                  <option key={m.id} value={m.id}>{m.firstName} {m.lastName}{m.age > 0 ? ` (${m.age} yrs)` : ''}</option>
                ))}
              </select>
            </div>

            {/* Relationship Type */}
            {relationship.relatedTo && (
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">{form.firstName ? `${form.firstName}'s relationship to` : 'New member\'s relationship to'} {(() => {
                  const m = (existingMembers || []).find(m => m.id === relationship.relatedTo);
                  return m ? `${m.firstName} ${m.lastName}` : 'them';
                })()}</label>
                <select
                  value={relationship.relationType}
                  onChange={(e) => setRelationship(prev => ({ ...prev, relationType: e.target.value }))}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-indigo-500"
                >
                  <option value="">Select relationship...</option>
                  {RELATIONSHIP_OPTIONS.map(group => (
                    <optgroup key={group.group} label={group.group}>
                      {group.options.map(opt => (
                        <option key={opt.value} value={opt.value}>{opt.label}</option>
                      ))}
                    </optgroup>
                  ))}
                </select>
              </div>
            )}

            {relationship.relatedTo && relationship.relationType && (
              <div className="mt-2 p-2 bg-indigo-50 rounded-lg text-xs text-indigo-700">
                {(() => {
                  const member = (existingMembers || []).find(m => m.id === relationship.relatedTo);
                  const memberName = member ? `${member.firstName} ${member.lastName}` : 'this person';
                  const allOpts = RELATIONSHIP_OPTIONS.flatMap(g => g.options);
                  const opt = allOpts.find(o => o.value === relationship.relationType);
                  return `${form.firstName || 'New member'} is the ${opt?.label || relationship.relationType} of ${memberName}`;
                })()}
              </div>
            )}
          </div>

          {errors.submit && <p className="text-xs text-red-600">{errors.submit}</p>}
          <div className="flex gap-3 pt-2">
            <button type="submit" disabled={creating}
              className="flex-1 px-4 py-2 text-sm font-medium text-white bg-indigo-600 rounded-lg hover:bg-indigo-700 disabled:opacity-50 transition-colors">
              {creating ? 'Adding...' : 'Add Member'}
            </button>
            <button type="button" onClick={onClose}
              className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors">
              Cancel
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

function InviteModal({ member, inviterId, onClose, onSent }) {
  const [contactMethod, setContactMethod] = useState('EMAIL');
  const [contactValue, setContactValue] = useState('');
  const [relationshipHint, setRelationshipHint] = useState('');
  const [permissionLevel, setPermissionLevel] = useState('VIEWER');
  const [sending, setSending] = useState(false);
  const [sent, setSent] = useState(false);
  const [error, setError] = useState('');
  const [createInvitation] = useMutation(CREATE_INVITATION);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!contactValue.trim()) {
      setError(contactMethod === 'EMAIL' ? 'Email is required' : 'Phone number is required');
      return;
    }

    if (!inviterId) {
      setError('Your profile is not set up yet. Please create your profile first.');
      return;
    }

    setSending(true);
    setError('');
    try {
      await createInvitation({
        variables: {
          input: {
            inviterId: parseInt(inviterId),
            inviteeId: parseInt(member.id),
            inviteeContact: contactValue.trim(),
            relationshipHint: relationshipHint.trim(),
            permissionLevel,
          },
        },
      });
      setSent(true);
      onSent();
    } catch (err) {
      console.error('Failed to send invitation:', err);
      setError('Failed to send invitation. Please try again.');
    } finally {
      setSending(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40" onClick={onClose}>
      <div className="bg-white rounded-xl shadow-xl w-full max-w-md mx-4 max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between px-4 py-3 border-b border-gray-200">
          <h2 className="text-base font-semibold text-gray-900">Invite to Family Tree</h2>
          <button onClick={onClose} className="p-1 text-gray-400 hover:text-gray-600"><X size={18} /></button>
        </div>

        {sent ? (
          <div className="p-6 text-center">
            <div className="w-14 h-14 mx-auto rounded-full bg-green-100 flex items-center justify-center mb-3">
              <Send size={24} className="text-green-600" />
            </div>
            <h3 className="text-lg font-semibold text-gray-900">Invitation Sent!</h3>
            <p className="text-sm text-gray-600 mt-1">
              You invited <strong>{member.firstName}{member.middleName ? " " + member.middleName : ""} {member.lastName}</strong> to join your family tree.
            </p>
            <div className="mt-4 bg-gray-50 rounded-lg p-3 text-left space-y-1.5 text-sm">
              <div className="flex justify-between">
                <span className="text-gray-500">Contact</span>
                <span className="font-medium">{contactValue}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-500">Permission</span>
                <span className="font-medium capitalize">{permissionLevel.toLowerCase()}</span>
              </div>
            </div>
            <button onClick={onClose} className="mt-5 px-4 py-2 text-sm font-medium text-white bg-indigo-600 rounded-lg hover:bg-indigo-700 transition-colors">
              Done
            </button>
          </div>
        ) : (
          <form onSubmit={handleSubmit} className="p-4 space-y-4">
            {/* Who */}
            <div className="flex items-center gap-3 p-3 bg-gray-50 rounded-lg">
              <div className={`w-10 h-10 rounded-full flex items-center justify-center text-sm font-bold flex-shrink-0 ${
                member.gender === 'male' ? 'bg-blue-100 text-blue-700' : 'bg-pink-100 text-pink-700'
              }`}>
                {`${member.firstName?.[0] || ''}${member.lastName?.[0] || ''}`.toUpperCase()}
              </div>
              <div>
                <p className="text-sm font-medium text-gray-900">{member.firstName}{member.middleName ? " " + member.middleName : ""} {member.lastName}</p>
                <p className="text-xs text-gray-500">Will receive your invitation</p>
              </div>
            </div>

            {/* Contact Method */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Contact Method</label>
              <div className="flex gap-2">
                {[
                  { key: 'EMAIL', label: 'Email', icon: 'mail' },
                  { key: 'PHONE', label: 'Phone', icon: 'phone' },
                ].map((m) => (
                  <button key={m.key} type="button" onClick={() => { setContactMethod(m.key); setContactValue(''); setError(''); }}
                    className={`flex-1 flex items-center justify-center gap-2 px-3 py-2 text-sm font-medium rounded-lg transition-colors ${
                      contactMethod === m.key ? 'bg-indigo-50 text-indigo-700 border border-indigo-200' : 'bg-white border border-gray-300 text-gray-600 hover:bg-gray-50'
                    }`}>
                    <AppIcon name={m.icon} size={16} />
                    {m.label}
                  </button>
                ))}
              </div>
            </div>

            {/* Contact Input */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                {contactMethod === 'EMAIL' ? 'Email Address' : 'Phone Number'} *
              </label>
              <input
                type={contactMethod === 'EMAIL' ? 'email' : 'tel'}
                value={contactValue}
                onChange={(e) => { setContactValue(e.target.value); setError(''); }}
                placeholder={contactMethod === 'EMAIL' ? 'relative@example.com' : '+1 (555) 123-4567'}
                className={`w-full px-3 py-2 border rounded-lg text-sm focus:ring-2 focus:ring-indigo-500 ${error ? 'border-red-300' : 'border-gray-300'}`}
              />
              {error && <p className="text-xs text-red-600 mt-1">{error}</p>}
            </div>

            {/* Relationship */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Relationship</label>
              <input type="text" value={relationshipHint} onChange={(e) => setRelationshipHint(e.target.value)}
                placeholder="e.g. Cousin on mother's side" className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-indigo-500" />
            </div>

            {/* Permission Level */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Permission Level</label>
              <div className="space-y-2">
                {[
                  { key: 'VIEWER', label: 'Viewer', desc: 'Can view family tree and memories' },
                  { key: 'CONTRIBUTOR', label: 'Contributor', desc: 'Can add memories and update profile' },
                  { key: 'ADMIN', label: 'Admin', desc: 'Full access to manage members and settings' },
                ].map((level) => (
                  <button key={level.key} type="button" onClick={() => setPermissionLevel(level.key)}
                    className={`w-full flex items-start gap-3 p-3 rounded-lg text-left transition-colors ${
                      permissionLevel === level.key ? 'bg-indigo-50 border border-indigo-200' : 'bg-white border border-gray-200 hover:bg-gray-50'
                    }`}>
                    <div className={`w-2 h-2 rounded-full mt-1.5 flex-shrink-0 ${permissionLevel === level.key ? 'bg-indigo-600' : 'bg-gray-300'}`} />
                    <div>
                      <p className={`text-sm font-medium ${permissionLevel === level.key ? 'text-indigo-700' : 'text-gray-900'}`}>{level.label}</p>
                      <p className="text-xs text-gray-500">{level.desc}</p>
                    </div>
                  </button>
                ))}
              </div>
            </div>

            <div className="flex gap-3 pt-2">
              <button type="submit" disabled={sending}
                className="flex-1 flex items-center justify-center gap-2 px-4 py-2 text-sm font-medium text-white bg-indigo-600 rounded-lg hover:bg-indigo-700 disabled:opacity-50 transition-colors">
                <Send size={14} />
                {sending ? 'Sending...' : 'Send Invitation'}
              </button>
              <button type="button" onClick={onClose}
                className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors">
                Cancel
              </button>
            </div>
          </form>
        )}
      </div>
    </div>
  );
}